import { useEffect, useRef } from "react";
import { fmtLong, roundDate } from "../hooks/useRounds";

/*
  The round identity block, shared by the landing sign-up card, the microsite
  round cards and the entry sheet (client: "same treatment", 2026-09-08):
    line 1  Round N
    line 2  <venue> - <layout> Circuit   (fitted to ONE line, capped at line 1's size)
    line 3  date in large tech numerals
  Colour is inherited from the card. `size` = "lg" (landing card) | "md" (grid cards).
*/
export function RoundTitle({ round, size = "lg", testidPrefix = "round-title", as: Tag = "h3" }) {
  const bodyRef = useRef(null);
  const roundRef = useRef(null);
  const circuitRef = useRef(null);

  useEffect(() => {
    const el = circuitRef.current;
    const box = bodyRef.current;
    if (!el || !box) return undefined;
    let raf = 0;
    // The fit depends on the container WIDTH only. iOS Safari fires `resize`
    // every time its toolbar collapses/expands mid-scroll (height only); on
    // the Ultimate Lap page that used to run 6 cards x 2 forced full-page
    // layouts per toolbar toggle (~100-400ms freeze while scrolling up,
    // scroll audit 2026-09-18). Skip when the width has not changed - unless
    // forced (fonts landing change the natural glyph width, not the box).
    let lastAvailW = -1;
    const availWidth = () => {
      const cs = getComputedStyle(box);
      return box.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    };
    const measure = (force) => {
      raf = 0;
      const availW = availWidth();
      if (!force && Math.round(availW) === Math.round(lastAvailW)) return;
      lastAvailW = availW;
      const cap = parseFloat(getComputedStyle(roundRef.current || el).fontSize) || 56;
      el.style.fontSize = "100px";
      const naturalW = el.getBoundingClientRect().width || 1;
      let target = Math.max(12, Math.min(cap, (availW / naturalW) * 100 * 0.995));
      el.style.fontSize = `${target}px`;
      // Glyph advance is not perfectly linear at small sizes (hinting/rounding):
      // re-measure at the target and correct (up to 3 passes) so it never spills.
      for (let i = 0; i < 3; i++) {
        const w1 = el.getBoundingClientRect().width;
        if (!(w1 > availW) || w1 <= 0) break;
        target = Math.max(12, target * (availW / w1) * 0.985);
        el.style.fontSize = `${target}px`;
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => measure(false)); };
    const scheduleForced = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => measure(true)); };
    // Initial + font-settle ticks are forced (glyph metrics change, width may not).
    scheduleForced();
    const timers = [400, 1200, 2500].map((ms) => setTimeout(scheduleForced, ms));
    document.fonts?.ready?.then(scheduleForced);
    // Width-gated: ResizeObserver also fires for height-only changes (including
    // the box growing because of our own fit), window resize for the iOS toolbar.
    const ro = "ResizeObserver" in window ? new ResizeObserver(schedule) : null;
    ro?.observe(box);
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [round?.circuit]);

  if (!round) return null;
  return (
    <div className={`gm-rt gm-rt--${size}`} ref={bodyRef} data-testid={`${testidPrefix}-block`}>
      <Tag className="gm-rt__title" data-testid={`${testidPrefix}-title`}>
        <span className="gm-rt__round" ref={roundRef} data-testid={`${testidPrefix}-round`}>Round {round.n}</span>
        <span className="gm-rt__circuit" ref={circuitRef} data-testid={`${testidPrefix}-circuit`}>{round.circuit}</span>
      </Tag>
      <p className="tech-num gm-rt__date" data-testid={`${testidPrefix}-date`}>
        <time dateTime={roundDate(round.date).toISOString()}>{fmtLong(round.date)}</time>
      </p>
    </div>
  );
}
